import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormComponent, FormComponentProps } from '../../../../components/form';
import css from './expandable-group.module.css';

export default function ExpandableGroup(
  name: string,
  body: FormComponent[],
  totals: {
    s: FormComponent;
    n: FormComponent;
    p2o5: FormComponent;
    k2o: FormComponent;
  },
  title: string,
  expanded: boolean = false
): FormComponent {
  return ({ ...rest }) => (
    <ExpandableGroupComponent
      name={name}
      body={body}
      totals={totals}
      title={title}
      expanded={expanded}
      {...rest}
    />
  );
}

interface Props extends FormComponentProps {
  name: string;
  body: FormComponent[];
  totals: {
    s: FormComponent;
    n: FormComponent;
    p2o5: FormComponent;
    k2o: FormComponent;
  };
  title?: string;
  expanded?: boolean;
}

export function ExpandableGroupComponent(props: Props) {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.fertilization-plan.plan.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const form = useFormContext();

  const [expanded, setExpanded] = useState(props.expanded ?? false);

  const n = useWatch({ name: 'needed_totals.n' });

  const children = props.body.map((Child: FormComponent, index: number) => {
    return <Child key={index} {...props} />;
  });

  const warning = useCallback(() => {
    if (props.name !== 'needed') {
      return null;
    }
    const needForPlannedHarvest = form.getValues(
      'needed.needForPlannedHarvest'
    );
    if (needForPlannedHarvest?.set && n && n > needForPlannedHarvest.nMax) {
      return <span>{tp('n_maximum_exceeded')}</span>;
    } else {
      return null;
    }
  }, [form, n, props.name, tp]);

  if (children.length === 0) {
    return null;
  }

  return (
    <fieldset className={css['group']}>
      <legend>
        <div className={css['title']} onClick={() => setExpanded(!expanded)}>
          {expanded ? (
            <FontAwesomeIcon icon={faChevronDown} />
          ) : (
            <FontAwesomeIcon icon={faChevronRight} />
          )}
          <span>{props.title}</span>
        </div>
        <div className={css['warning']}>{warning()}</div>
        <div className={css['totals']}>
          {<props.totals.s />}
          {<props.totals.n />}
          {<props.totals.p2o5 />}
          {<props.totals.k2o />}
        </div>
      </legend>
      <div className={`${css['set']} ${expanded ? css['expanded'] : ''}`}>
        <div className={css['fields']}>{children}</div>
      </div>
    </fieldset>
  );
}
